import axios from 'axios'
import {
  getEngines as getFallbackEngines,
  getMakes as getFallbackMakes,
  getModels as getFallbackModels,
  getYears as getFallbackYears,
  parseEngine,
} from '@shopbay/shared'

export function resolveApiBaseUrl(): string {
  const configured = (import.meta.env.VITE_API_URL as string | undefined)?.trim()
  if (!configured || configured === '/api') {
    return '/api'
  }

  // In local development, force same-origin API requests through Vite proxy to keep auth cookies stable.
  if (typeof window !== 'undefined') {
    const currentHost = window.location.hostname
    const isCurrentLoopback = currentHost === 'localhost' || currentHost === '127.0.0.1'

    if (isCurrentLoopback) {
      try {
        const parsed = new URL(configured)
        const isConfiguredLoopback =
          parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1'

        if (isConfiguredLoopback) {
          return '/api'
        }
      } catch {
        // Fall through to configured value.
      }
    }
  }

  return configured
}

const api = axios.create({
  baseURL: resolveApiBaseUrl(),
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Important: send cookies with requests
})

// Public customer-facing routes: a 401 there means an invalid/expired link,
// not a missing staff session — the pages render their own message and the
// anonymous customer must never be bounced to the staff login screen.
// The marketing landing at / is public too: its session probe must be
// allowed to 401 without redirecting the visitor to /login.
const PUBLIC_PATH_PREFIXES = ['/q/', '/approve/', '/login']
const isPublicPath = (path: string) =>
  path === '/' || PUBLIC_PATH_PREFIXES.some((prefix) => path.startsWith(prefix))

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      if (!isPublicPath(window.location.pathname)) {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

export default api

function inferFallbackFuelType(engineLabel: string) {
  const parsed = parseEngine(engineLabel)
  if (parsed.isElectric) return 'electric'
  if (parsed.isHybrid) return 'hybrid'
  if (parsed.isDiesel) return 'diesel'
  return 'gasoline'
}

function getFallbackEngineOptions(year: number, make: string, model: string) {
  return getFallbackEngines(year, make, model).map((engineLabel) => {
    const parsed = parseEngine(engineLabel)
    return {
      description: engineLabel,
      displacementL: parsed.displacementL,
      cylinders: parsed.cylinders,
      fuelType: inferFallbackFuelType(engineLabel),
    }
  })
}

export async function createDemoSession(): Promise<{ redirectPath?: string }> {
  const { data } = await api.post('/auth/demo')
  return data.data
}

export async function completeOnboarding(payload: {
  shopName: string
  phone: string
}) {
  const { data } = await api.post('/auth/onboarding', payload)
  return data.data
}

export type WaitlistSource = 'email_otp' | 'google_oauth' | 'direct'

export interface WaitlistSubmission {
  id: string
  email: string
  source: WaitlistSource
  marketingOptIn: boolean
  userAgent?: string | null
  ipAddress?: string | null
  createdAt: string
  updatedAt: string
  alreadyJoined: boolean
}

export async function joinWaitlist(payload: {
  email: string
  marketingOptIn?: boolean
  source?: WaitlistSource
}): Promise<WaitlistSubmission> {
  const { data } = await api.post('/auth/waitlist', payload)
  return data.data
}

export async function fetchJobs(shopId: string, search?: string) {
  const params = new URLSearchParams({ shopId })
  if (search) params.append('search', search)
  const { data } = await api.get(`/jobs?${params}`)
  return data.data
}

export async function fetchJob(id: string) {
  const { data } = await api.get(`/jobs/${id}`)
  return data.data
}

export async function createJob(job: any) {
  const { data } = await api.post('/jobs', job)
  return data.data
}

export async function updateJob(id: string, updates: any) {
  const { data } = await api.patch(`/jobs/${id}`, updates)
  return data.data
}

export async function updateJobMileage(
  id: string,
  payload: {
    mileage: number
    confirmLowerMileage?: boolean
  }
) {
  const { data } = await api.post(`/jobs/${id}/mileage`, payload)
  return data
}

export async function fetchQuotes(shopId: string, jobId?: string) {
  const params = new URLSearchParams({ shopId })
  if (jobId) params.append('jobId', jobId)
  const { data } = await api.get(`/quotes?${params}`)
  return data.data
}

export async function fetchQuote(id: string) {
  const { data } = await api.get(`/quotes/${id}`)
  return data.data
}

export async function createQuote(quote: any) {
  const { data } = await api.post('/quotes', quote)
  return data.data
}

export async function sendQuote(id: string, options?: { sendEmail?: boolean }) {
  const { data } = await api.post(
    `/quotes/${id}/send`,
    options?.sendEmail ? { sendEmail: true } : undefined,
  )
  // approvalUrl is always returned so the shop can still copy/text the link;
  // emailSent reports the outcome of the optional Mailgun delivery.
  return {
    quote: data.data,
    approvalUrl: data.approvalUrl as string | undefined,
    emailSent: Boolean(data.emailSent),
    emailError: data.emailError as string | undefined,
  }
}

export async function approveQuote(token: string, approval: any) {
  const { data } = await api.post(`/q/${token}/approve`, approval)
  return data.data
}

export async function declineQuote(token: string, reason?: string) {
  const { data } = await api.post(`/q/${token}/decline`, { reason })
  return data.data
}

export async function fetchInvoices(shopId: string) {
  const { data } = await api.get(`/invoices?shopId=${shopId}`)
  return data.data
}

export async function fetchInvoice(id: string) {
  const { data } = await api.get(`/invoices/${id}`)
  return data.data
}

export async function createInvoiceFromQuote(quoteId: string) {
  const { data } = await api.post(`/invoices/from-quote/${quoteId}`)
  return data.data
}

export async function fetchCustomers(shopId: string, search?: string) {
  const params = new URLSearchParams({ shopId })
  if (search) params.append('search', search)
  const { data } = await api.get(`/customers?${params}`)
  return data.data
}

export async function fetchCustomer(id: string) {
  const { data } = await api.get(`/customers/${id}`)
  return data.data
}

export async function createCustomer(customer: any) {
  const { data } = await api.post('/customers', customer)
  return data.data
}

export async function updateCustomer(id: string, updates: any) {
  const { data } = await api.patch(`/customers/${id}`, updates)
  return data.data
}

export async function fetchVehicles(params?: { customerId?: string; shopId?: string }) {
  const query = new URLSearchParams()
  if (params?.customerId) query.set('customerId', params.customerId)
  if (params?.shopId) query.set('shopId', params.shopId)
  const suffix = query.toString() ? `?${query.toString()}` : ''
  const { data } = await api.get(`/vehicles${suffix}`)
  return data.data
}

export async function fetchVehicle(id: string) {
  const { data } = await api.get(`/vehicles/${id}`)
  return data.data
}

export async function fetchVehicleHistory(id: string) {
  const { data } = await api.get(`/vehicles/${id}/history`)
  return data.data
}

export async function createVehicle(vehicle: any) {
  const { data } = await api.post('/vehicles', vehicle)
  return data.data
}

export async function updateVehicle(id: string, updates: any) {
  const { data } = await api.patch(`/vehicles/${id}`, updates)
  return data.data
}

export async function mergeVehicle(id: string, targetVehicleId: string) {
  const { data } = await api.post(`/vehicles/${id}/merge`, { targetVehicleId })
  return data.data
}

// VIN Decode
export async function decodeVin(vin: string, shopId?: string) {
  const params = new URLSearchParams()
  if (shopId) params.set('shopId', shopId)
  const suffix = params.toString() ? `?${params.toString()}` : ''
  const { data } = await api.get(`/vehicles/vin/${vin}/decode${suffix}`)
  return data.data
}

// VIN Validation (for live validation)
export async function validateVin(vin: string, shopId?: string) {
  const params = new URLSearchParams()
  if (shopId) params.set('shopId', shopId)
  const suffix = params.toString() ? `?${params.toString()}` : ''
  const { data } = await api.get(`/vehicles/vin/${vin}/validate${suffix}`)
  return data.data
}

// Vehicle Data endpoints for Year/Make/Model dropdowns.
// Responses are HTTP-cached by the browser (Cache-Control on the API), so
// bump this version to invalidate every client's cache after a catalog change.
const VEHICLE_DATA_CACHE_VERSION = 2

export async function fetchVehicleYears() {
  try {
    const { data } = await api.get(`/vehicle-data/years?v=${VEHICLE_DATA_CACHE_VERSION}`)
    if (Array.isArray(data.data) && data.data.length > 0) {
      return data.data
    }
  } catch (error) {
    console.warn('Falling back to local vehicle years dataset', error)
  }

  return getFallbackYears()
}

export async function fetchVehicleMakes(year: number) {
  try {
    const { data } = await api.get(`/vehicle-data/makes?year=${year}&v=${VEHICLE_DATA_CACHE_VERSION}`)
    if (Array.isArray(data.data) && data.data.length > 0) {
      return data.data
    }
  } catch (error) {
    console.warn(`Falling back to local vehicle makes dataset for year ${year}`, error)
  }

  return getFallbackMakes(year)
}

export async function fetchVehicleModels(year: number, make: string) {
  try {
    const { data } = await api.get(`/vehicle-data/models?year=${year}&make=${encodeURIComponent(make)}&v=${VEHICLE_DATA_CACHE_VERSION}`)
    if (Array.isArray(data.data) && data.data.length > 0) {
      return data.data
    }
  } catch (error) {
    console.warn(`Falling back to local vehicle models dataset for ${year} ${make}`, error)
  }

  return getFallbackModels(year, make)
}

export async function fetchVehicleEngines(year: number, make: string, model: string) {
  try {
    const { data } = await api.get(`/vehicle-data/engines?year=${year}&make=${encodeURIComponent(make)}&model=${encodeURIComponent(model)}&v=${VEHICLE_DATA_CACHE_VERSION}`)
    if (Array.isArray(data.data) && data.data.length > 0) {
      return data.data
    }
  } catch (error) {
    console.warn(`Falling back to local vehicle engines dataset for ${year} ${make} ${model}`, error)
  }

  return getFallbackEngineOptions(year, make, model)
}

export async function fetchTemplates(shopId: string) {
  const { data } = await api.get(`/templates?shopId=${shopId}`)
  return data.data
}

export async function requestTemplateUploadUrl(shopId: string, filename: string, contentType: string) {
  const { data } = await api.post('/templates/upload-url', { shopId, filename, contentType })
  return data
}

export async function createTemplateRecord(template: any) {
  const { data } = await api.post('/templates', template)
  return data.data
}

export async function processTemplate(id: string) {
  const { data } = await api.post(`/templates/${id}/process`)
  return data.data
}

// Catalog
export async function fetchCatalogItems(shopId: string, search?: string, type?: string, category?: string) {
  const params = new URLSearchParams({ shopId })
  if (search) params.append('search', search)
  if (type) params.append('type', type)
  if (category) params.append('category', category)
  const { data } = await api.get(`/catalog?${params}`)
  return data.data
}

export async function createCatalogItem(shopId: string, item: any) {
  const { data } = await api.post(`/catalog?shopId=${shopId}`, item)
  return data.data
}

export async function updateCatalogItem(id: string, updates: any) {
  const { data } = await api.patch(`/catalog/${id}`, updates)
  return data.data
}

export async function deleteCatalogItem(id: string) {
  const { data } = await api.delete(`/catalog/${id}`)
  return data.data
}

export interface CatalogDefaultItem {
  id: string
  code: string
  name: string
  description?: string | null
  salePrice: string
  unit: string
  category: string
  type: 'labor' | 'part' | 'subcontract' | 'misc'
  sortOrder: number
}

export interface CatalogDefaultProfile {
  id: string
  key: string
  name: string
  description?: string | null
  config: Record<string, unknown>
}

export async function fetchCatalogDefaultItems(): Promise<CatalogDefaultItem[]> {
  const { data } = await api.get('/catalog/defaults')
  return data.data
}

export async function applyCatalogDefaults(
  shopId: string,
  codes?: string[]
): Promise<{ created: number; skipped: number; total: number }> {
  const { data } = await api.post('/catalog/defaults/apply', { shopId, codes })
  return data.data
}

export async function fetchCatalogDefaultProfiles(key?: string): Promise<CatalogDefaultProfile[]> {
  const suffix = key ? `?key=${encodeURIComponent(key)}` : ''
  const { data } = await api.get(`/catalog/default-profiles${suffix}`)
  return data.data
}

// Service Groups
export async function fetchServiceGroups(shopId: string, search?: string) {
  const params = new URLSearchParams({ shopId })
  if (search) params.append('search', search)
  const { data } = await api.get(`/service-groups?${params}`)
  return data.data
}

export async function fetchServiceGroup(id: string) {
  const { data } = await api.get(`/service-groups/${id}`)
  return data.data
}

export async function createServiceGroup(shopId: string, group: any) {
  const { data } = await api.post(`/service-groups?shopId=${shopId}`, group)
  return data.data
}

export async function updateServiceGroup(id: string, updates: any) {
  const { data } = await api.patch(`/service-groups/${id}`, updates)
  return data.data
}

export async function deleteServiceGroup(id: string) {
  const { data } = await api.delete(`/service-groups/${id}`)
  return data.data
}

// Quote Line Items
export async function addQuoteLineItems(quoteId: string, lineItems: any[]) {
  const { data } = await api.post(`/quotes/${quoteId}/line-items`, { lineItems })
  return data.data
}

export async function updateQuoteLineItem(quoteId: string, lineItemId: string, updates: any) {
  const { data } = await api.patch(`/quotes/${quoteId}/line-items/${lineItemId}`, updates)
  return data.data
}

export async function deleteQuoteLineItem(quoteId: string, lineItemId: string) {
  const { data } = await api.delete(`/quotes/${quoteId}/line-items/${lineItemId}`)
  return data.data
}

export async function addServiceGroupToQuote(quoteId: string, serviceGroupId: string) {
  const { data } = await api.post(`/quotes/${quoteId}/add-service-group`, { serviceGroupId })
  return data.data
}

// Shop defaults
export async function fetchShopDefaults(shopId: string) {
  const { data } = await api.get(`/shops/defaults?shopId=${shopId}`)
  return data.data
}

export async function updateShopDefaults(shopId: string, updates: any) {
  const { data } = await api.patch('/shops/defaults', { shopId, ...updates })
  return data.data
}

export interface ShopProfile {
  id: string
  name: string
  logoUrl?: string | null
  brandColor?: string | null
  email?: string | null
  phone?: string | null
  notes?: string | null
  address?: string | null
  city?: string | null
  state?: string | null
  zip?: string | null
  supportTier?: 'standard' | 'premium'
  createdAt?: string
  updatedAt?: string
}

export interface ShopStats {
  vehicles: number
  customers: number
  openJobs: number
  invoicedJobs: number
  members: number
}

export type ShopMemberRole = 'owner' | 'admin' | 'tech' | 'read'

export interface ShopMember {
  id: string
  userId: string
  name: string
  email: string
  phone?: string | null
  avatarUrl?: string | null
  role: ShopMemberRole
  joinedAt: string
}

export async function fetchShopProfile(shopId: string): Promise<ShopProfile> {
  const { data } = await api.get(`/shops/profile?shopId=${shopId}`)
  return data.data
}

export async function updateShopProfile(
  shopId: string,
  updates: Partial<ShopProfile>
): Promise<ShopProfile> {
  const { data } = await api.patch('/shops/profile', { shopId, ...updates })
  return data.data
}

export async function fetchShopStats(shopId: string): Promise<ShopStats> {
  const { data } = await api.get(`/shops/stats?shopId=${shopId}`)
  return data.data
}

export async function fetchShopMembers(shopId: string): Promise<{
  currentUserRole: ShopMemberRole
  members: ShopMember[]
}> {
  const { data } = await api.get(`/shops/members?shopId=${shopId}`)
  return data.data
}

export async function inviteShopMember(
  shopId: string,
  payload: {
    email: string
    role: ShopMemberRole
    name?: string
    phone?: string
  }
): Promise<ShopMember> {
  const { data } = await api.post('/shops/members', { shopId, ...payload })
  return data.data
}

export async function updateShopMemberRole(
  shopId: string,
  memberId: string,
  role: ShopMemberRole
): Promise<ShopMember> {
  const { data } = await api.patch(`/shops/members/${memberId}`, { shopId, role })
  return data.data
}

export async function removeShopMember(shopId: string, memberId: string): Promise<void> {
  await api.delete(`/shops/members/${memberId}?shopId=${shopId}`)
}

export async function uploadShopLogo(shopId: string, file: File): Promise<string> {
  const formData = new FormData()
  formData.append('shopId', shopId)
  formData.append('file', file)

  const { data } = await api.post('/shops/logo', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })

  return data.data.logoUrl as string
}

// Support reports
export async function uploadSupportScreenshot(shopId: string | null, file: File) {
  const contentType = file.type || 'application/octet-stream'
  const payload: Record<string, string> = {
    entityType: 'photo',
    entityId: 'support-report',
    filename: file.name,
    contentType,
  }

  if (shopId) {
    payload.shopId = shopId
  }

  const { data: signed } = await api.post('/uploads/sign', payload)

  const uploadResult = await fetch(signed.uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType,
    },
    body: file,
    // The upload URL is the API's authenticated proxy — the session cookie
    // must ride along (cross-origin in production).
    credentials: 'include',
  })

  if (!uploadResult.ok) {
    throw new Error(`Screenshot upload failed (${uploadResult.status})`)
  }

  return signed.publicUrl as string
}

export async function submitSupportReport(report: {
  message: string
  screenshotUrl?: string | null
  route?: string
  shopId?: string
  metadata?: Record<string, unknown>
}) {
  const { data } = await api.post('/support/report', report)
  return data.data
}

export async function fetchSupportReports(params?: { shopId?: string; status?: 'open' | 'resolved' }) {
  const query = new URLSearchParams()
  if (params?.shopId) query.set('shopId', params.shopId)
  if (params?.status) query.set('status', params.status)

  const suffix = query.toString() ? `?${query.toString()}` : ''
  const { data } = await api.get(`/support/admin${suffix}`)
  return data.data
}

export async function fetchSupportReport(id: string) {
  const { data } = await api.get(`/support/admin/${id}`)
  return data.data
}

export async function resolveSupportReport(id: string) {
  const { data } = await api.patch(`/support/admin/${id}/resolve`)
  return data.data
}

// ===== NEW JOB-BASED SYSTEM APIs =====

// Job Line Items
export async function fetchJobLineItems(jobId: string) {
  const { data } = await api.get(`/jobs/${jobId}/items`)
  return data.data
}

export async function createJobLineItem(jobId: string, item: {
  type: "labor" | "part" | "subcontract" | "misc"
  description: string
  notes?: string
  quantity: number
  unitPrice: number
  markupPercent?: number
  catalogItemId?: string
}) {
  const { data } = await api.post(`/jobs/${jobId}/items`, item)
  return data.data
}

export async function updateJobLineItem(
  jobId: string,
  itemId: string,
  updates: Partial<{
    type: "labor" | "part" | "subcontract" | "misc"
    description: string
    notes: string
    quantity: number
    unitPrice: number
    markupPercent: number
    catalogItemId: string
    sortOrder: number
  }>
) {
  const { data } = await api.patch(`/jobs/${jobId}/items/${itemId}`, updates)
  return data.data
}

export async function deleteJobLineItem(jobId: string, itemId: string) {
  const { data } = await api.delete(`/jobs/${jobId}/items/${itemId}`)
  return data.data
}

// Save all line items at once (recalculates totals)
export async function saveJobLineItems(
  jobId: string,
  payload: {
    lineItems: Array<{
      id?: string
      type: "labor" | "part" | "subcontract" | "misc"
      description: string
      notes?: string
      quantity: number
      unitPrice: number
      markupPercent: number
      catalogItemId?: string
      sortOrder: number
    }>
    fees?: number
    discount?: number
  }
) {
  const { data } = await api.post(`/jobs/${jobId}/save-items`, payload)
  return data
}

// Job Stage Transitions
export async function transitionJobStage(
  jobId: string,
  to: "quote" | "work_order" | "hold" | "invoice"
) {
  const { data } = await api.post(`/jobs/${jobId}/transition`, { to })
  return data
}

// Job Approvals
export async function fetchJobApprovals(jobId: string) {
  const { data } = await api.get(`/jobs/${jobId}/approvals`)
  return data.data
}

export async function createJobApproval(
  jobId: string,
  approval: {
    approvedByName: string
    approvedByEmail?: string
    signatureData?: string
  }
) {
  const { data } = await api.post(`/jobs/${jobId}/approvals`, approval)
  return data.data
}

export async function sendJobApprovalLink(
  jobId: string,
  options?: { sendEmail?: boolean },
) {
  const { data } = await api.post(
    `/jobs/${jobId}/approvals/send-link`,
    options?.sendEmail ? { sendEmail: true } : undefined,
  )
  return data
}

// Public approval (for magic links)
export async function fetchPublicJobApproval(token: string) {
  const { data } = await api.get(`/q/approve/${token}`)
  return data
}

export async function approveJobViaMagicLink(
  token: string,
  approval: { approvedByName: string; signatureData: string }
) {
  const { data } = await api.post(`/q/approve/${token}`, approval)
  return data
}

// Job Payments
export async function fetchJobPayments(jobId: string) {
  const { data } = await api.get(`/jobs/${jobId}/payments`)
  return data.data
}

export async function createJobPayment(
  jobId: string,
  payment: {
    amount: number
    provider: "cash" | "venmo" | "check" | "other"
    notes?: string
  }
) {
  const { data } = await api.post(`/jobs/${jobId}/payments`, payment)
  return data.data
}

export async function updateJobPayment(
  jobId: string,
  paymentId: string,
  updates: {
    status?: "pending" | "completed" | "failed" | "refunded"
    notes?: string
  }
) {
  const { data } = await api.patch(`/jobs/${jobId}/payments/${paymentId}`, updates)
  return data.data
}

export async function deleteJobPayment(jobId: string, paymentId: string) {
  const { data } = await api.delete(`/jobs/${jobId}/payments/${paymentId}`)
  return data.data
}
